import { C8AttributeId, C8NodeType } from "./node";
import { cnl } from "./value";

export type DataStoreId = string;

export const enum DataStorePrefixes {
	ALL = "all",
	RELATION = "relation",
	SOURCE = "source",
	SUBTYPE = "subtype",
};

export interface IDataStoreId {
    /** type of data store id determined by prefix */
	prefix: DataStorePrefixes;
	
    /** targget class of the data store */
    target: string;
}

/** Data Components for Global Data Store. Example: (T)TargetClass(P)all/name or Example: (T)TargetClass(P)all */
export interface IGlobalDataStoreId extends IDataStoreId {
    /** indicates if custom (not OOTB) store */
    custom?: boolean;

	/** Name of the Data Store */
    name?: string;
}

/** Data Components for Relation Data Store. Example: (T)TargetClass(P)relation/SourceClass/RelationId */
export interface IRelationDataStoreId extends IDataStoreId {
    /** Relation id of the Data Store */
    relation: C8AttributeId;

    /** Source Class of the Data Store.  */
    sourceClass: C8NodeType; 
}

/** Data Components for Contextual Data Store. Example: (T)TargetClass(P)source/SourceClass/name */
export interface IContextualDataStoreId extends IDataStoreId {
    /** indicates if custom (not OOTB) store */
    custom?: boolean;

    /** Name of the Data Store */
    name: string;

    /** Source Class of the Data Store.  */
    sourceClass: C8NodeType; 
}

/** Data Components for Subtype Data Store. Example: Example: (T)TargetSubtypeClass(P)subtype/all/SubtypedClass, (T)TargetSubtypeClass(P)subtype/active/SubtypedClass */
export interface ISubtypeDataStoreId extends IDataStoreId {
    /** determines if data store returns only active subtypes or all subtypes */
    onlyActiveSubtypes: boolean;

    /** Subtyped class that holds the subtype. (Formulated Product to Formulated Product Type). DataStore  */
    subtypedClass: C8NodeType;
}

export interface IDataStoreSort {
	/** if true, then sorted in descending order (Z-A) rather than ascending (A-Z) */
	descending?: boolean;

	/** attribute being sorted by */
	id?: C8AttributeId;

	/** path to attribute being sorted by */
	path?: string;

	/** node property to sort by - server does not currently support this in data stores, but client does in Relationship Sort tag */
	prop?: string;
}

export interface IDataStoreInfo {
	/** indicates if data store can be filtered */
	canFilter?: boolean;

	/** indicates if data store can be grouped (if it can be filtered, then normally can be grouped) */
	canGroup?: boolean;

	/** indicates if paging is supported on data store (as opposed to just returning all) */
	canPage?: boolean;

	/** indicates if data store can be sorted by attributes other than default */
	canSort?: boolean;

	/** indicates true if there is only one limit path, every step of the path is either singular (ref) or a vector, and no steps use the Parent operator */
	isOrdered?: boolean;

	/** indicates the traversal paths from source class to target class */
	limitPaths?: string[];

	/** path hints applicable to view (if any) */
	pathHintsId?: string;

	/** query string used to retrieve the data (for informational purposes only, not any logic) */
	query?: string;

	/** indicates true if any limit path in the query includes a step that is not composite */
	readOnly?: boolean;

	/** the single limit path in the query (can only be one) so long as that path is singular except for possibly the last step. Null if the path is not a simple path */
	simplePath?: string;

	/** sorting applied to data store; if an ordered data store, then this will be empty */
	sortAttributes?: IDataStoreSort[];
}

export interface IDataStore {
	/** id of data store */
	dataStoreId: DataStoreId;

	/** source class of data store - could be empty if global */
	sourceClass?: C8NodeType;

	/** target class of data store */
	targetClass: C8NodeType;

	/** information about data store */
	info: IDataStoreInfo;
}

export interface IDataStoreNamesItem {
	/** node name */
	name: string;

	/** node url */
	url: cnl;
}

export interface IDataStoreNamesResponse {
	/** list of name/url information */
	urls: IDataStoreNamesItem[];
}

export interface IDataStoreParams {
	/** id of data store */
	dataStoreId: DataStoreId;

	/** ad-hoc XML query */
	queryXml?: string;
	
	/** source node url for contextual data stores */
	sourceUrl?: cnl;
}
